import { DestroyRef, inject, Injectable } from '@angular/core';
import { combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { ContextService, EventService } from '@services';
import {
  ACTIVITY_EVENTS,
  APP_EVENT_KEY,
  APP_TIMEOUT_SETTING_KEY,
  APP_WARNING_TIMEOUT_SETTING_KEY,
  DEFAULT_APP_TIMEOUT,
  DEFAULT_APP_WARNING_TIMEOUT,
  WORKER_ACTION,
} from '@models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsService } from '@services/webapi/settings.service';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  #areListenersSetup = false;
  #timeLeft$: Subject<number> = new Subject<number>();
  #worker: Worker;
  #eventService: EventService = inject(EventService);
  #contextService: ContextService = inject(ContextService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #stopListening$: Subject<void> = new Subject<void>();
  #settingsService: SettingsService = inject(SettingsService);

  constructor() {
    this.#worker = new Worker(new URL('./idle-timer.worker', import.meta.url));

    this.#worker.onmessage = ({ data }) => {
      if (data.timeLeft !== undefined) {
        this.#timeLeft$.next(data.timeLeft);
        this.#eventService.emitEvent(APP_EVENT_KEY.TIME_LEFT, data.timeLeft);
        this.#eventService.emitEvent(
          APP_EVENT_KEY.SHOW_LOGOUT_WARNING,
          data.warning
        );
      }

      if (data.logout) {
        this.#eventService.emitEvent(APP_EVENT_KEY.LOGOUT_BY_TIMEOUT);
      }
    };

    combineLatest([
      this.#contextService.currentLocation$,
      this.#settingsService.getAll(),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([, settings]) => {
        const idleTimeout = settings.getNumberOrDefault(
          APP_TIMEOUT_SETTING_KEY,
          DEFAULT_APP_TIMEOUT
        );
        const warningTimeout = settings.getNumberOrDefault(
          APP_WARNING_TIMEOUT_SETTING_KEY,
          DEFAULT_APP_WARNING_TIMEOUT
        );

        if (this.#contextService.isInMainRoutes) {
          if (!this.#areListenersSetup) {
            this.#setupActivityListeners(idleTimeout, warningTimeout);
            this.#areListenersSetup = true;
          }

          this.startIdleTimer(idleTimeout, warningTimeout);
        } else {
          this.disableActivityListeners();
          this.stopIdleTimer();
          this.#areListenersSetup = false;
        }
      });
  }

  public resetIdleTimer(
    idleTimeoutSeconds: number,
    warningTimeoutSeconds: number
  ): void {
    if (!this.#areListenersSetup) {
      this.#setupActivityListeners(idleTimeoutSeconds, warningTimeoutSeconds);
      this.#areListenersSetup = true;
    }
    this.#worker.postMessage({ action: WORKER_ACTION.STOP });
    this.#worker.postMessage({
      idleTimeout: idleTimeoutSeconds,
      warningTimeout: warningTimeoutSeconds,
      action: WORKER_ACTION.START,
    });
  }

  public stopIdleTimer(): void {
    this.#worker.postMessage({ action: WORKER_ACTION.STOP });
  }

  public getTimeLeft(): Observable<number> {
    return this.#timeLeft$.asObservable();
  }

  public startIdleTimer(
    idleTimeoutSeconds: number,
    warningTimeoutSeconds: number
  ): void {
    this.#worker.postMessage({
      idleTimeout: idleTimeoutSeconds,
      warningTimeout: warningTimeoutSeconds,
      action: WORKER_ACTION.START,
    });
  }

  public disableActivityListeners(): void {
    this.#stopListening$.next();
    this.#areListenersSetup = false;
  }

  #setupActivityListeners(idleTimeout: number, warningTimeout: number): void {
    ACTIVITY_EVENTS.forEach(event => {
      fromEvent(document, event)
        .pipe(
          map((): boolean => true),
          tap((): void => this.resetIdleTimer(idleTimeout, warningTimeout)),
          takeUntil(this.#stopListening$),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe();
    });
  }
}
